import React, { useState, useEffect, useCallback } from 'react';
import '../../../App.css';
import edit from './../../../images/edit.svg';
import expanded from './../../../images/expand.png';
import Select, { components } from 'react-select';
import { talentService, locationService, companyService } from '../../../api/apiService';

// Format timestamp helper
const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Define the TruncatedContent component with company rating support
const TruncatedContent = ({ content, type, isGoodPerformer = false, isBadPerformer = false, isExpanded = false, onToggle }) => {
  if (!content) return null;

  let displayContent;
  if (type === "notes") {
    displayContent = content;
  } else if (type === "companies") {
    // Handle company data with timestamps and ratings
    if (isExpanded) {
      // When expanded, show each company on a separate line with full details
      if (Array.isArray(content)) {
        return (
          <div onClick={onToggle} style={{
            color: isGoodPerformer ? "#37b24d" : isBadPerformer ? "#ff6b6b" : "inherit",
            cursor: 'pointer',
            display: 'block',
            width: '100%',
            fontSize: '0.85em' // Smaller font for companies
          }}>
            {content.map((c, index) => {
              const companyName = c.label || c.name || c;
              const timestamp = c.timestamp ? ` (${formatTimestamp(c.timestamp)})` : '';
              const rating = c.rating ? ` [Rating ${c.rating}]` : '';
              return (
                <div key={index} style={{ 
                  marginBottom: '2px',
                  padding: '1px 2px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '2px',
                  fontSize: '0.83rem', // Even smaller font for individual companies
                  fontWeight: '300' // Remove bold styling
                }}>
                  {companyName}{rating}{timestamp}
                </div>
              );
            })}
          </div>
        );
      } else {
        displayContent = content;
      }
    } else {
      // When collapsed, show truncated list
      displayContent = Array.isArray(content)
        ? content.map(c => {
            const companyName = c.label || c.name || c;
            const timestamp = c.timestamp ? ` (${formatTimestamp(c.timestamp)})` : '';
            const rating = c.rating ? ` [Rating ${c.rating}]` : '';
            return `${companyName}${rating}${timestamp}`;
          }).join(", ")
        : content;
    }
  } else if (type === "locations") {
    // Handle location data
    displayContent = Array.isArray(content)
      ? content.map(loc => loc.label || loc.name || loc.short || loc).join(", ")
      : content;
  } else {
    displayContent = Array.isArray(content)
      ? content.map(c => c.label || c.name || c).join(", ")
      : content;
  }

  const style = {
    color: isGoodPerformer ? "#37b24d" : isBadPerformer ? "#ff6b6b" : "inherit",
    cursor: 'pointer',
    display: 'inline-block',
    width: '100%',
    fontSize: type === "notes" ? '0.8rem' : '0.85em' // Even smaller font for notes
  };

  return (
    <div onClick={onToggle} style={style}>
      {isExpanded ? displayContent : (displayContent.length > 40 ? `${displayContent.substring(0, 40)}...` : displayContent)}
    </div>
  );
};

// Add DropdownIndicator component
const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <div style={{ width: "16px", margin: "0" }}>
          <svg
            version="1.1"
            id="Isolationsmodus"
            x="0px"
            y="0px"
            viewBox="0 0 322.9 257.78"
            shapeRendering="geometricPrecision"
          >
            <path
              xmlns="http://www.w3.org/2000/svg"
              d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z"
            />
          </svg>
        </div>
      </components.DropdownIndicator>
    )
  );
};

// Add positions options
const positions = [
  { value: "Hair & Make-Up", label: "Hair & Make-Up" },
  { value: "Art Direktion", label: "Art Direktion" },
  { value: "Foto", label: "Foto" },
  { value: "Foto Assist", label: "Foto Assist" },
  { value: "Video", label: "Video" },
  { value: "Digi-OP", label: "Digi-OP" },
  { value: "Styling", label: "Styling" },
  { value: "Styling Assist", label: "Styling Assist" },
  { value: "LOGISTIK / WARENAUFBEREITUNG", label: "LOGISTIK / WARENAUFBEREITUNG" },
  { value: "PRODUCER", label: "PRODUCER" },
  { value: "Art Buying", label: "Art Buying" }, 
  { value: "Account Management", label: "Account Management" },
  { value: "AI Content Production", label: "AI Content Production" }
];

// Add notes options
const notesOption = [
  {value: 'LEGER', label:'LEGER'},
  {value: 'HÄNGER', label:'HÄNGER'},
  {value: 'BÜSTE', label:'BÜSTE'},
  {value: 'ACC', label:'ACC'}
];

// Add rating options
const ratingOptions = [
  { value: 1, label: 'Rating 1' },
  { value: 2, label: 'Rating 2' }
];

function OldTalentList() {
  const [search, setSearch] = useState("");
  const [talents, setTalents] = useState([]);
  const [allTalents, setAllTalents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTalent, setCurrentTalent] = useState(null);
  const [currentName, setCurrentName] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [currentFunction, setCurrentFunction] = useState("");
  const [currentSurname, setCurrentSurname] = useState("");
  const [currentPhone, setCurrentPhone] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");
  const [selectedGoodCompanies, setSelectedGoodCompanies] = useState([]);
  const [selectedBadCompanies, setSelectedBadCompanies] = useState([]);
  const [selectedtryoutCompanies, setSelectedtryoutCompanies] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [notesProductLeads, setNotesProductLeads] = useState('');
  const [notesProductLeadsdd, setNotesProductLeadsdd] = useState(null);

  // Filters
  const [selectedType, setSelectedType] = useState("");
  const [selectedFunction, setSelectedFunction] = useState("");
  const [filterGoodCompanies, setFilterGoodCompanies] = useState([]);
  const [filterBadCompanies, setFilterBadCompanies] = useState([]);
  const [filtertryoutCompanies, setFilterTryoutCompanies] = useState([]);
  const [filterLocation, setfilterloation] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isExpanded, setIsExpanded] = useState(true);

  // Add state for options
  const [companyOptions, setCompanyOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  // Custom style for selects
  const customStyles = {
    control: (base) => ({
      ...base,
      minWidth: "150px",
      borderColor: "black",
      borderWidth: "1px",
      boxShadow: "none",
      margin: "5px",
      borderRadius: "0px",
      color: "black",
      fontWeight: "700",
      height: "auto",
      textTransform: "uppercase",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "700",
    }),
    input: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "700",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "black" : "inherit",
      "&:hover": { backgroundColor: state.isSelected ? "black" : "#f7f7f7" },
    }),
  };

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
  };

  // Update fetchTalents to fetch all data at once
  const fetchTalents = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch all talents without filters
      const response = await talentService.getAllTalents(1, 1000);
      
      // Handle response format
      const talentsData = response?.data?.talents || response?.data || [];
      
      if (Array.isArray(talentsData)) {
        // For each talent, fetch company relationships from the new endpoint
        const enhancedTalentsData = await Promise.all(talentsData.map(async (talent) => {
          try {
            // Try to get company data from the new endpoint
            const companyResponse = await talentService.getTalentCompanies(talent.id);
            
            // Check for nested data structure
            if (companyResponse && companyResponse.data) {
              const companyData = companyResponse.data;
              
              // Merge the company data with the talent data
              const enhancedTalent = {
                ...talent,
                goodPerformanceCompany: companyData.goodPerformanceCompany || [],
                badPerformanceCompany: companyData.badPerformanceCompany || [],
                tryOutCompany: companyData.tryOutCompany || []
              };
              return enhancedTalent;
            }
            
            // If endpoint fails, return original talent data
            return talent;
          } catch (error) {
            // Just return the original talent data if there's an error
            console.warn(`Could not fetch company data for talent ${talent.id}:`, error);
            return talent;
          }
        }));
        
        setAllTalents(enhancedTalentsData);
      } else {
        console.error('Unexpected response format:', response);
        setAllTalents([]);
      }
    } catch (error) {
      console.error('Error fetching talents:', error);
      setAllTalents([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch companies from API
  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies(1, 1000);
      const companiesData = response?.data?.companies || response?.data || [];
      
      const formattedCompanies = companiesData
        .map(company => ({
          value: company.id || company.value,
          label: company.name || company.label
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setCompanyOptions(formattedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Fetch locations from API
  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations(1, 1000);
      const locationsData = response?.data?.locations || response?.data || [];
      
      const formattedLocations = locationsData
        .map(location => ({
          value: location.id,
          label: location.name || location.short
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setLocationOptions(formattedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Add filter function
  const applyFilters = useCallback(() => {
    let filtered = [...allTalents];

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(talent => 
        (talent.name || talent.Name || '').toLowerCase().includes(searchLower) ||
        (talent.email || talent.E_Mail || '').toLowerCase().includes(searchLower) ||
        (talent.notes || talent.Notiz_Setcast || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply function filter
    if (selectedFunction) {
      filtered = filtered.filter(talent => 
        (talent.function || talent.Funktion) === selectedFunction
      );
    }

    // Apply type filter
    if (selectedType) {
      filtered = filtered.filter(talent => 
        (talent.type || talent.bereich || talent.Bereich) === selectedType
      );
    }

    // Apply company filters
    if (filterGoodCompanies.length > 0) {
      filtered = filtered.filter(talent => {
        const talentCompanies = talent.goodPerformanceCompany || [];
        return filterGoodCompanies.some(filter => 
          talentCompanies.some(company => 
            (typeof company === 'string' ? company : company.label || company.name).toLowerCase() === filter.label.toLowerCase()
          )
        );
      });
    }

    if (filterBadCompanies.length > 0) {
      filtered = filtered.filter(talent => {
        const talentCompanies = talent.badPerformanceCompany || [];
        return filterBadCompanies.some(filter => 
          talentCompanies.some(company => 
            (typeof company === 'string' ? company : company.label || company.name).toLowerCase() === filter.label.toLowerCase()
          )
        );
      });
    }

    if (filtertryoutCompanies.length > 0) {
      filtered = filtered.filter(talent => {
        const talentCompanies = talent.tryOutCompany || talent.tryout_company || [];
        return filtertryoutCompanies.some(filter => 
          talentCompanies.some(company => 
            (typeof company === 'string' ? company : company.label || company.name).toLowerCase() === filter.label.toLowerCase()
          )
        );
      });
    }

    // Apply location filter with improved logic
    if (filterLocation.length > 0) {
      filtered = filtered.filter(talent => {
        // Get talent locations, handling different possible data structures
        const talentLocations = Array.isArray(talent.locations) ? talent.locations : 
                              Array.isArray(talent.location) ? talent.location :
                              talent.location ? [talent.location] : [];

        // Check if any of the selected locations match the talent's locations
        return filterLocation.some(selectedLocation => {
          const selectedLocationLabel = selectedLocation.label.toLowerCase();
          
          return talentLocations.some(talentLocation => {
            // Handle different location data formats
            const locationLabel = typeof talentLocation === 'string' ? 
                                talentLocation.toLowerCase() : 
                                (talentLocation.label || talentLocation.name || '').toLowerCase();
            
            return locationLabel === selectedLocationLabel;
          });
        });
      });
    }

    setTalents(filtered);
  }, [allTalents, search, selectedFunction, selectedType, filterGoodCompanies, filterBadCompanies, filtertryoutCompanies, filterLocation]);

  // Update filter handlers to use client-side filtering
  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const handleTypeChange = useCallback((option) => {
    setSelectedType(option ? option.value : "");
  }, []);

  const handleFunctionChange = useCallback((option) => {
    setSelectedFunction(option ? option.value : "");
  }, []);

  // Apply filters whenever any filter changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Initialize data
  useEffect(() => {
    fetchTalents();
    fetchCompanies();
    fetchLocations();
  }, []);
  
  // Effect to expand all rows by default
  useEffect(() => {
    const allTalentIds = talents.map(talent => talent.id);
    setExpandedRows(new Set(allTalentIds));
    setIsExpanded(true);
  }, [talents]);

  // Toggle expanding/collapsing all rows
  const toggleAllRows = () => {
    if (isExpanded) {
      setExpandedRows(new Set());
    } else {
      const allTalentIds = talents.map(talent => talent.id);
      setExpandedRows(new Set(allTalentIds));
    }
    setIsExpanded(!isExpanded);
  };

  // Toggle individual row expansion
  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Function options
  const functionOptions = Array.from(new Set(talents.map(talent => 
    talent.function || talent.Funktion)
  ))
  .filter(Boolean)
  .map(func => ({
    value: func,
    label: func
  }));

  // Type options
  const typeOptions = Array.from(new Set(talents.map(talent => 
    talent.type || talent.bereich || talent.Bereich)
  ))
  .filter(Boolean)
  .map(type => ({
    value: type,
    label: type
  }));

  // Helper function to ensure company data has consistent format
  const ensureCompanyFormat = (companies) => {
    if (!companies || !Array.isArray(companies)) return [];
    
    return companies.map(company => {
      if (typeof company === 'string') {
        return {
          label: company,
          value: company,
          timestamp: new Date().toISOString()
        };
      } else if (typeof company === 'object') {
        return {
          label: company.label || company.name || '',
          value: company.value || company.id || '',
          rating: company.rating || undefined, // Preserve rating
          timestamp: company.timestamp || new Date().toISOString()
        };
      }
      return company;
    });
  };

  // Edit talent function
  const editTalent = async (talent) => {
    setIsEditing(true);
    setCurrentTalent(talent);
    setCurrentEmail(talent.email || talent.E_Mail);
    setCurrentName(talent.name || talent.Name);
    setCurrentFunction(talent.function || talent.Funktion);
    setCurrentSurname(talent.notes || talent.Notiz_Setcast);
    setCurrentPhone(talent.phone || talent.Telefon);
    setCurrentPrice(talent.rate || talent.Tagessatz);
    
    // Set notes
    setNotesProductLeads(talent.notesProductLeads || '');
    setNotesProductLeadsdd(talent.notesProductLeadsdd || null);
    
    try {
      // Fetch company relationships from API
      const response = await talentService.getTalentCompanies(talent.id);
      
      if (response && response.data) {
        const { goodPerformanceCompany, badPerformanceCompany, tryOutCompany } = response.data;
        
        // Format company data to ensure consistent structure
        const formattedGoodCompanies = ensureCompanyFormat(goodPerformanceCompany || []);
        const formattedBadCompanies = ensureCompanyFormat(badPerformanceCompany || []);
        const formattedTryoutCompanies = ensureCompanyFormat(tryOutCompany || []);
        
        setSelectedGoodCompanies(formattedGoodCompanies);
        setSelectedBadCompanies(formattedBadCompanies);
        setSelectedtryoutCompanies(formattedTryoutCompanies);
      }
    } catch (error) {
      console.error('Error fetching company relationships:', error);
      const formattedGoodCompanies = ensureCompanyFormat(talent.goodPerformanceCompany || []);
      const formattedBadCompanies = ensureCompanyFormat(talent.badPerformanceCompany || []);
      const formattedTryoutCompanies = ensureCompanyFormat(talent.tryOutCompany || []);
      
      setSelectedGoodCompanies(formattedGoodCompanies);
      setSelectedBadCompanies(formattedBadCompanies);
      setSelectedtryoutCompanies(formattedTryoutCompanies);
    }
  };



  // Update talent details function
  const updateTalentDetails = async () => {
    if (currentTalent) {
      try {
        // Basic talent info update
        const basicInfo = {
          notesProductLeads: notesProductLeads,
          notesProductLeadsdd: notesProductLeadsdd
        };
        
        // Update basic talent info
        await talentService.updateTalent(currentTalent.id, basicInfo);
        
        // Get the original companies from the API
        const originalResponse = await talentService.getTalentCompanies(currentTalent.id);
        
        // Check if we have original data to compare against
        if (originalResponse && originalResponse.data) {
          const originalData = originalResponse.data;
          
          // Helper function to find companies that are in newList but not in originalList
          const findNewCompanies = (newList, originalList) => {
            return newList.filter(newCompany => {
              return !originalList.some(origCompany => 
                origCompany.value === newCompany.value
              );
            });
          };
          
          // Helper function to find companies that are in originalList but not in newList
          const findRemovedCompanies = (newList, originalList) => {
            return originalList.filter(origCompany => {
              return !newList.some(newCompany => 
                newCompany.value === origCompany.value
              );
            });
          };
          
          // Find new and removed companies for each category
          const newGoodCompanies = findNewCompanies(selectedGoodCompanies, originalData.goodPerformanceCompany || []);
          const removedGoodCompanies = findRemovedCompanies(selectedGoodCompanies, originalData.goodPerformanceCompany || []);
          
          const newBadCompanies = findNewCompanies(selectedBadCompanies, originalData.badPerformanceCompany || []);
          const removedBadCompanies = findRemovedCompanies(selectedBadCompanies, originalData.badPerformanceCompany || []);
          
          const newTryoutCompanies = findNewCompanies(selectedtryoutCompanies, originalData.tryOutCompany || []);
          const removedTryoutCompanies = findRemovedCompanies(selectedtryoutCompanies, originalData.tryOutCompany || []);
          
          // Only send updates if there are changes
          if (
            newGoodCompanies.length > 0 || removedGoodCompanies.length > 0 ||
            newBadCompanies.length > 0 || removedBadCompanies.length > 0 ||
            newTryoutCompanies.length > 0 || removedTryoutCompanies.length > 0
          ) {
            // Helper function to get the final list after adding new and removing old companies
            const getFinalList = (originalList, newCompanies, removedCompanies) => {
              // Start with original list
              const result = [...originalList];
              
              // Remove items that should be removed
              const afterRemoval = result.filter(item => 
                !removedCompanies.some(removed => removed.value === item.value)
              );
              
              // Add new items with current timestamp, preserving any existing ratings
              return [
                ...afterRemoval,
                ...newCompanies.map(company => ({
                  label: company.label,
                  value: company.value,
                  rating: company.rating || undefined, // Preserve rating if it exists
                  timestamp: new Date().toISOString()
                }))
              ];
            };
            
            // Prepare the final data for the API
            const updatedCompanyData = {
              goodPerformanceCompany: getFinalList(originalData.goodPerformanceCompany || [], newGoodCompanies, removedGoodCompanies),
              badPerformanceCompany: getFinalList(originalData.badPerformanceCompany || [], newBadCompanies, removedBadCompanies),
              tryOutCompany: getFinalList(originalData.tryOutCompany || [], newTryoutCompanies, removedTryoutCompanies)
            };
            
            // Send the update
            const updateResponse = await talentService.updateTalentCompanies(currentTalent.id, updatedCompanyData);
            console.log('Company update response:', updateResponse);
          }
        }
        
        // Refresh talent list
        fetchTalents();
        setIsEditing(false);
      } catch (error) {
        console.error('Error updating talent:', error);
        alert('Error updating talent. Please try again.');
      }
    }
  };



  return (
    <div className="content-wrapper page">
      <div className='content-wrapper'>
        <h1>Talent Rating</h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px' }}>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
            className='inputtext search'
          />
        </div>

        <div style={{ display: 'flex', marginTop: '10px' }}>
        <div style={{ marginRight: '10px' }}>
          <Select
            value={typeOptions.find(option => option.value === selectedType)}
            onChange={handleTypeChange}
            options={typeOptions}
            isClearable
            placeholder="Select Type"
            styles={customStyles}
            />
        </div>
<div>
          <Select
            value={functionOptions.find(option => option.value === selectedFunction)}
            onChange={handleFunctionChange}
            options={functionOptions}
            isClearable
            placeholder="Select Function"
            styles={customStyles}
          />
        </div>
        <div>
            <Select
              isMulti
              value={filterGoodCompanies}
              onChange={setFilterGoodCompanies}
              options={companyOptions}
              isClearable
              placeholder="Select Good Performance"
              styles={customStyles}
            />
          </div>
          <div>
            <Select
              isMulti
              value={filtertryoutCompanies}
              onChange={setFilterTryoutCompanies}
              options={companyOptions}
              isClearable
              placeholder="Select Try Out"
              styles={customStyles}
            />
          </div>
          <div>
            <Select
              isMulti
              value={filterBadCompanies}
              onChange={setFilterBadCompanies}
              options={companyOptions}
              isClearable
              placeholder="Select Bad Performance"
              styles={customStyles}
            />
          </div>
          <div>
            <Select
              isMulti
              value={filterLocation}
              onChange={setfilterloation}
              options={locationOptions}
              isClearable
              placeholder="Select Location"
              styles={customStyles}
            />
          </div>
          <div style={{ marginLeft: "10px", display: "flex", alignItems: "center" }}>
            <button 
              className='button'
              onClick={toggleAllRows}
            >
              {isExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
          </div>
      </div>
      
      <div className='content-wrapper content-wrapper-talent-list'>
        <div className="talent-list">
          <div className='talent-item-header'>
            <div style={{ flexBasis: '300px' }}>Name</div>
            <div style={{ flexBasis: '150px' }}>Type</div>
            <div style={{ flexBasis: '200px' }}>Function</div>
            <div style={{ flexBasis: '300px' }}>Good Performance</div>
            <div style={{ flexBasis: '300px' }}>Bad Performance</div>
            <div style={{ flexBasis: '300px' }}>Try Out</div>
            <div style={{ flexBasis: '300px' }}>Notes Product Leads</div>
            <div style={{ flexBasis: '100px' }}></div>
          </div>
          
          {isLoading ? (
            <div className="loading">Loading talents...</div>
          ) : (
            talents.sort((a, b) => {
              const nameA = (a.name || a.Name || '').toLowerCase();
              const nameB = (b.name || b.Name || '').toLowerCase();
              if (nameA < nameB) return -1;
              if (nameA > nameB) return 1;
              return 0;
          }).map((talent) => (
            <div key={talent.id} className='talent-item'>
              {isEditing && currentTalent.id === talent.id ? (
                  <div className="talent-item-edit-form">
                    <span style={{ paddingLeft: '0' }} className='talent-item-header'>{currentName}</span>
          
                    <div className="edit-form-grid">
                      <div className="edit-form-field">
                        <span className='talent-item-input-title'>Good Performance Companies</span>
                        <Select
                          isMulti
                          value={selectedGoodCompanies}
                          onChange={setSelectedGoodCompanies}
                          options={companyOptions}
                          isClearable
                          placeholder="Good Performance Companies"
                          styles={customStyles}
                        />
                      </div>
                      
                      <div className="edit-form-field">
                        <span className='talent-item-input-title'>Bad Performance Companies</span>
                        <Select
                          isMulti
                          value={selectedBadCompanies}
                          onChange={setSelectedBadCompanies}
                          options={companyOptions}
                          isClearable
                          placeholder="Bad Performance Companies"
                          styles={customStyles}
                        />
                      </div>
                      
                      <div className="edit-form-field">
                        <span className='talent-item-input-title'>Try Out Companies</span>
                        <Select
                          isMulti
                          value={selectedtryoutCompanies}
                          onChange={setSelectedtryoutCompanies}
                          options={companyOptions}
                          isClearable
                          placeholder="Try Out Companies"
                          styles={customStyles}
                        />
                      </div>
                      
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Notes Product Leads</span>
                        <input
                          className="inputtext"
                          value={notesProductLeads}
                          onChange={(e) => setNotesProductLeads(e.target.value)}
                        />
                      </div>
                      
                      <div className="edit-form-field">
                        <span className="talent-item-input-title">Notes Type</span>
                        <Select
                          value={notesProductLeadsdd}
                          onChange={setNotesProductLeadsdd}
                          options={notesOption}
                          isClearable
                          placeholder="Notes Type"
                          styles={customStyles}
                        />
                      </div>
                    </div>

                    <div className="edit-form-actions">
                  <button className='button' onClick={updateTalentDetails} style={{ marginTop: '10px', marginBottom: '20px' }}>Save</button>
                  <button className='button' onClick={() => setIsEditing(false)} style={{ marginTop: '10px', marginBottom: '20px' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='talent-item-firstline'>
                      <div style={{ flexBasis: '300px' }}>{talent.name || talent.Name}</div>
                      <div style={{ flexBasis: '150px' }}>{talent.type || talent.bereich || talent.Bereich}</div>
                      <div style={{ flexBasis: '200px' }}>{talent.function || talent.Funktion}</div>
                  <div style={{ flexBasis: '300px' }}>
                    <TruncatedContent 
                      content={talent.goodPerformanceCompany || []} 
                      type="companies"
                      isGoodPerformer={true}
                      isExpanded={expandedRows.has(talent.id)}
                      onToggle={() => toggleRow(talent.id)}
                    />
                  </div>
                  <div style={{ flexBasis: '300px' }}>
                    <TruncatedContent 
                      content={talent.badPerformanceCompany || []} 
                      type="companies"
                      isBadPerformer={true}
                      isExpanded={expandedRows.has(talent.id)}
                      onToggle={() => toggleRow(talent.id)}
                    />
                  </div>
                  <div style={{ flexBasis: '300px' }}>
                    <TruncatedContent 
                      content={talent.tryOutCompany || talent.tryout_company || []} 
                      type="companies"
                      isExpanded={expandedRows.has(talent.id)}
                      onToggle={() => toggleRow(talent.id)}
                    />
                  </div>
                  <div style={{ flexBasis: '300px' }}>  
                        {talent.notesProductLeads} {talent.notesProductLeadsdd ? `- ${talent.notesProductLeadsdd.label || ''}` : ''}
</div>
                      <div style={{ flexBasis: '100px', display: "flex", gap: "8px" }}>
                        <button
                          className="toggle-buttons"
                          onClick={() => toggleRow(talent.id)}
                        >
                          <img
                            src={expanded}
                            alt={expandedRows.has(talent.id) ? "Collapse" : "Expand"}
                            className="edit-container"
                            style={{
                              transform: expandedRows.has(talent.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease'
                            }}
                          />
                        </button>
                        <button className='edit-button' onClick={() => editTalent(talent)}>
                          <img src={edit} alt="Edit" className="edit-container" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
                  </div>
            ))
              )}
        </div>
      </div>
    </div>
  );
}

export default OldTalentList;